import {
  type FunctionComponent,
  type ReactNode,
  useRef,
  useState,
} from 'react';

import { Button } from '@dr.pogodin/react-utils';

import { FilterModal } from 'components/FilterModal';

import Throbber from 'components/Throbber';
import { useVideos } from 'hooks/video';

import type {
  DifficultyT,
  FilterT,
  ModalityT,
  VideoIntentT,
  VideoT,
} from 'utils';

import IconFilter from 'assets/images/ai/icon-filter.svg';

import whitePillButtonTheme from 'themes/Button/white-pill-h40.scss';

import { VideoCard } from '../VideoCard';

import './style.scss';

type HeapT = {
  loadMoreNode?: HTMLDivElement | null;
  loadMoreObserver?: IntersectionObserver;
  videosNode?: HTMLDivElement | null;
};

type PropsT = {
  addVideoToEnd: (video: VideoT) => void;
};

export const ClipsBrowser: FunctionComponent<PropsT> = ({
  addVideoToEnd,
}) => {
  const { current: heap } = useRef<HeapT>({});

  const [showFilterModal, setShowFilterModal] = useState(false);

  const [artistIdFilter, setArtistIdFilter] = useState<FilterT<string>>();

  const [
    durationMin,
    setDurationMin,
  ] = useState<[number, number] | undefined>();

  const [
    difficultyFilter,
    setDifficultyFilter,
  ] = useState<FilterT<DifficultyT>>();

  const [intentFilter, setIntentFilter] = useState<FilterT<VideoIntentT>>();
  const [modalityFilter, setModalityFilter] = useState<FilterT<ModalityT>>();

  const videos = useVideos({
    query: {
      artistIds: artistIdFilter,
      asEndUser: true,
      difficulty: difficultyFilter,
      durationMin,
      intents: intentFilter,
      modalities: modalityFilter,
    },
  });

  const nodes: ReactNode[] = [];

  for (const video of videos.items) {
    nodes.push(
      <VideoCard
        draggable
        key={video.id}
        onAdd={() => {
          addVideoToEnd(video);
        }}
        video={video}
      />,
    );
  }

  if (videos.loading) {
    nodes.push(
      <div key="throbber" styleName="throbberContainer"><Throbber /></div>,
    );
  }

  const manageLoadMoreObserver = () => {
    if (heap.loadMoreNode && heap.videosNode) {
      if (!heap.loadMoreObserver) {
        heap.loadMoreObserver = new IntersectionObserver(([e]) => {
          if (e?.isIntersecting) videos.loadMore?.();
        }, {
          root: heap.videosNode,
        });
        heap.loadMoreObserver.observe(heap.loadMoreNode);
      }
    } else if (heap.loadMoreObserver) {
      heap.loadMoreObserver.disconnect();
      delete heap.loadMoreObserver;
    }
  };

  if (videos.loadMore) {
    nodes.push(
      <div
        key="loadMore"
        ref={(node) => {
          heap.loadMoreNode = node;
          manageLoadMoreObserver();
        }}
      />,
    );
  }

  if (!nodes.length) {
    nodes.push(<div key="nothing-found" styleName="noVideosFound">Nothing found</div>);
  }

  return (
    <div styleName="container">
      <div styleName="content">
        <div styleName="header">
          <h2 styleName="subHeader">
            Add Segments You Like
          </h2>
          <Button
            onClick={() => {
              setShowFilterModal(true);
            }}
            theme={whitePillButtonTheme}
          >
            <IconFilter styleName="noShrink" />
            <span styleName="filterLabel">Filter</span>
          </Button>
        </div>
        <div
          ref={(node) => {
            heap.videosNode = node;
            manageLoadMoreObserver();
          }}
          styleName="videosContainer"
        >
          {nodes}
        </div>
      </div>
      {showFilterModal ? (
        <FilterModal
          artist={{ initial: artistIdFilter, onChanged: setArtistIdFilter }}
          difficulty={{
            initial: difficultyFilter,
            onChanged: setDifficultyFilter,
          }}
          duration={{ initial: durationMin, onChanged: setDurationMin }}
          intent={{ initial: intentFilter, onChanged: setIntentFilter }}
          modality={{ initial: modalityFilter, onChanged: setModalityFilter }}
          onClose={() => {
            setShowFilterModal(false);
          }}
        />
      ) : null}
    </div>
  );
};
